import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useDashboardChrome } from '../../hooks/useDashboardChrome';
import { botIcon, botLabel, statusColor, statusLabel } from '../../utils/botHelpers';

export default function DashboardHome() {
  const navigate = useNavigate();
  const { bots, userData, currentUser } = useDashboardChrome();

  const [dbTables, setDbTables] = useState<any[]>([]);
  const [recentRows, setRecentRows] = useState<any[]>([]);
  const [dbLoading, setDbLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/db/tables').then(r => r.json()).then(d => {
      if (d.success) {
        const tables: any[] = d.tables || [];
        setDbTables(tables);
        const target = tables.find(t => ['orders', 'leads', 'enquiries', 'contacts', 'submissions'].includes(t.slug)) || tables[0];
        if (target && target.rowCount > 0) {
          apiFetch(`/api/db/tables/${target.slug}/rows`).then(r2 => r2.json()).then(d2 => {
            if (d2.success) setRecentRows([...d2.rows].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 8));
          }).catch(() => {});
        }
      }
      setDbLoading(false);
    }).catch(() => setDbLoading(false));
  }, []);

  const activeBots   = bots.filter(b => b.status === 'active').length;
  const totalRecords = dbTables.reduce((s, t) => s + (t.rowCount || 0), 0);
  const today        = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const plan     = userData?.plan || 'free';
  const isFree   = plan === 'free';
  const isTrial  = userData?.paymentStatus === 'trial';
  let billingLine = 'Free plan';
  if (isTrial && userData?.trialEndsAt) {
    const days = Math.max(0, Math.ceil((userData.trialEndsAt - Date.now()) / 86400000));
    billingLine = `Trial · ${days}d left`;
  } else if (!isFree && userData?.lastPaymentDate && userData?.billingCycle) {
    const next = new Date(userData.lastPaymentDate + (userData.billingCycle === 'monthly' ? 30 : 365) * 86400000);
    billingLine = `Renews ${next.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  const checkItems = [
    { done: bots.length > 0,                       label: 'Create your first bot' },
    { done: bots.some(b => b.status === 'active'), label: 'Get a bot live' },
    { done: dbTables.length > 0,                   label: 'Create a database table' },
    { done: !isFree,                                label: 'Upgrade your plan' },
    { done: !!(currentUser?.displayName && currentUser?.photoURL), label: 'Complete your profile' },
  ];
  const setupDone = checkItems.every(c => c.done);

  const sc = { background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '14px', padding: '20px' } as React.CSSProperties;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-page)', overflow: 'auto' }}>
      {/* Header */}
      <div style={{ background: 'var(--bg-card)', padding: '20px 28px', borderBottom: '1px solid var(--bg-element)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700', margin: 0 }}>
            {currentUser?.displayName ? `Hi, ${currentUser.displayName.split(' ')[0]} 👋` : 'Welcome back 👋'}
          </h1>
          <p style={{ color: 'var(--text-faint)', fontSize: '12px', margin: '3px 0 0' }}>{today}</p>
        </div>
        <Link to="/create" style={{ background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '9px 20px', borderRadius: '9px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>
          + New Bot
        </Link>
      </div>

      <div style={{ padding: '28px', maxWidth: '1000px' }}>
        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          <div style={sc}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>🤖</div>
            <div style={{ fontSize: '26px', fontWeight: '700', color: 'var(--text-primary)' }}>{bots.length}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Total bots{activeBots > 0 ? ` · ${activeBots} live` : ''}
            </div>
          </div>
          <div style={sc}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>🛒</div>
            <div style={{ fontSize: '26px', fontWeight: '700', color: 'var(--text-primary)' }}>{dbLoading ? '…' : totalRecords}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Records in database</div>
          </div>
          <div style={sc}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>🗄️</div>
            <div style={{ fontSize: '26px', fontWeight: '700', color: 'var(--text-primary)' }}>{dbLoading ? '…' : dbTables.length}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Database tables</div>
          </div>
          <div style={{ ...sc, borderColor: (isFree ? '#64748b' : '#5B4FE8') + '30', background: (isFree ? '#64748b' : '#5B4FE8') + '08' }}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>💳</div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', lineHeight: 1.3 }}>{billingLine}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{plan.charAt(0).toUpperCase() + plan.slice(1)} plan</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: setupDone ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          {/* Setup checklist */}
          {!setupDone && (
            <div style={sc}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>🚀 Finish setting up</div>
              {checkItems.map(({ done, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: done ? 'none' : '2px solid var(--card-border)', background: done ? '#10b981' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {done && <span style={{ color: 'white', fontSize: '10px' }}>✓</span>}
                  </div>
                  <span style={{ fontSize: '12px', color: done ? 'var(--text-faint)' : 'var(--text-primary)', textDecoration: done ? 'line-through' : 'none' }}>{label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Your bots — clicking opens the bot's overview */}
          <div style={sc}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>🤖 Your bots</div>
              <Link to="/create" style={{ fontSize: '11px', color: '#818cf8', textDecoration: 'none' }}>+ New</Link>
            </div>
            {bots.length === 0
              ? <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>No bots yet — <Link to="/create" style={{ color: '#818cf8' }}>create one</Link></div>
              : bots.map(b => (
                <div key={b.id} onClick={() => navigate(`/dashboard/bots/${b.id}/overview`)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '4px' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#5B4FE810')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>{botIcon(b)}</span>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{b.businessName}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{botLabel(b)}</div>
                    </div>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 7px', borderRadius: '8px', background: statusColor(b.status) + '20', color: statusColor(b.status) }}>{statusLabel(b.status)}</span>
                </div>
              ))
            }
          </div>
        </div>

        {/* Recent database records */}
        {!dbLoading && dbTables.length > 0 && (
          <div style={{ ...sc, marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>🛒 Recent database records</div>
              <Link to="/database" style={{ fontSize: '11px', color: '#818cf8', textDecoration: 'none' }}>View all →</Link>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const, marginBottom: recentRows.length > 0 ? '12px' : 0 }}>
              {dbTables.map(t => (
                <span key={t.slug} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '8px', background: 'var(--bg-element)', color: 'var(--text-muted)' }}>
                  {t.name} · {t.rowCount} rows
                </span>
              ))}
            </div>
            {recentRows.length === 0
              ? <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '6px' }}>No records yet — your flow will write here automatically.</div>
              : (() => {
                const target = dbTables.find(t => ['orders', 'leads', 'enquiries'].includes(t.slug)) || dbTables[0];
                const fields = target?.fields || [];
                return (
                  <div style={{ overflowX: 'auto' as const }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '12px' }}>
                      <tbody>
                        {recentRows.map(row => {
                          const mainVal = fields[0] ? String(row[fields[0].key] ?? '') : '';
                          const subVal  = fields[1] ? String(row[fields[1].key] ?? '') : '';
                          return (
                            <tr key={row.id} style={{ borderBottom: '1px solid var(--bg-element)' }}>
                              <td style={{ padding: '9px 0', width: '50%' }}>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{mainVal || row.id}</div>
                                {subVal && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{subVal}</div>}
                              </td>
                              <td style={{ padding: '9px 0', textAlign: 'right' as const, fontSize: '11px', color: 'var(--text-faint)', whiteSpace: 'nowrap' as const }}>
                                {row.createdAt ? new Date(row.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            }
          </div>
        )}

        {/* Empty first-time state */}
        {bots.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🤖</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>Create your first bot</div>
            <div style={{ fontSize: '13px', color: 'var(--text-faint)', lineHeight: '1.6', marginBottom: '24px' }}>
              Set up a bot that chats with your customers 24/7 on WhatsApp, your website, or Facebook.
            </div>
            <Link to="/create" style={{ display: 'inline-block', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '12px 28px', borderRadius: '9px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
              Create my first bot →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
